import React from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import AddIcon from "@mui/icons-material/Add";
import DisableEntriesIcon from "@mui/icons-material/WebAssetOff";
import EnableEntriesIcon from "@mui/icons-material/WebAsset";
import Alert from "@mui/material/Alert";
import CustomSnackbar from "../../Feedback/CustomSnackbar";
import getSaveErrorMessages from "../../../functions/general/get-save-error-messages";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { selectMissions, selectRooms, selectSelectedRoom } from "../../../store/room/room-selectors";
import { selectScheduleEntries, selectSchedulesRestSet } from "../../../store/room/scheduler/scheduler-selectors";
import { isCurrentTimeScheduleEntry, isScheduleInFloor } from "../../../interfaces/robot/scheduling/schedule-entry";
import { isExcludedDateWithReason } from "../../../utils/date";
import ReadOnlyProtectedButton from "../../Inputs/ReadOnlyProtectedButton";
import { selectExcludedDates } from "../../../store/robot-configurations/robot-configurations-selectors";
import { isHttpError } from "../../../interfaces/api/http-error";
import { errorResetResponse, isErrorSetOpen, isSuccessSetOpen, successResetResponse } from "../../../interfaces/api/rest-set";

const SchedulerActions: React.FC<{ floorId: string; onNewSchedulerEntryClick: () => void }> = ({
    floorId,
    onNewSchedulerEntryClick,
}) => {
    const { t } = useTranslation();

    const matchesSmUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));

    const dispatch = useSharedDispatch();

    const { patchSchedulesRestSet, patchEnableAllSchedules, patchDisableAllSchedules } = useSharedActions();

    const roomData = useSharedSelector((state) => selectSelectedRoom(state));
    const excludedDates = useSharedSelector((state) => selectExcludedDates(state));
    const scheduleEntries = useSharedSelector((state) => selectScheduleEntries(state));
    const schedulesRestSet = useSharedSelector((state) => selectSchedulesRestSet(state));
    const missions = useSharedSelector((state) => selectMissions(state));
    const rooms = useSharedSelector((state) => selectRooms(state));
    
    const roomId = roomData?.id;

    const enableAllEntriesClickHandler = () => {
        if (!roomData) return;
        dispatch(patchEnableAllSchedules(roomData.floorId));
    };

    const disableAllEntriesClickHandler = () => {
        if (!roomData) return;
        dispatch(patchDisableAllSchedules(roomData.floorId));
    };

    const savingSnackbarCloseHandler = () => {
        dispatch(patchSchedulesRestSet(successResetResponse(schedulesRestSet)));
    };

    const errorSnackBarCloseHandler = () => {
        dispatch(patchSchedulesRestSet(errorResetResponse(schedulesRestSet)));
    };

    const hasNewCurrentScheduleEntry =
      scheduleEntries.data.some((s) => isCurrentTimeScheduleEntry(s.scheduleEntry) && s.scheduleEntry.enabled) &&
      isExcludedDateWithReason(
        DateTime.now().weekday,
        excludedDates.data?.filter((d) => d.roomIds.some((id) => id === roomId)) ?? null,
        floorId
      ) === undefined;

    return (
        <React.Fragment>
            <Stack direction="row" mt={1} pl={1} width="100%" gap={2}>
                {roomData?.secondaryStation == null ? (
                    <React.Fragment>
                        <ReadOnlyProtectedButton
                            id="new-schedule-entry-button"
                            onClick={onNewSchedulerEntryClick}
                            disabled={roomData?.secondaryStation != null}
                            startIcon={<AddIcon />}
                            sx={{ fontSize: matchesSmUp ? "medium" : "small" }}
                        >{matchesSmUp ? t("scheduler-new-schedule-entry") : t("button-new")}</ReadOnlyProtectedButton>
                        <ReadOnlyProtectedButton
                            id="button-enable-all-entries"
                            onClick={enableAllEntriesClickHandler}
                            disabled={roomData?.secondaryStation != null || 
                                scheduleEntries.data.filter((se) => isScheduleInFloor( se.scheduleEntry, floorId, missions, rooms ) && !se.scheduleEntry.enabled).length === 0}
                            startIcon={matchesSmUp && <EnableEntriesIcon />}
                            sx={{ fontSize: matchesSmUp ? "medium" : "small" }}
                        >{t("scheduler-enable-all-entries")}</ReadOnlyProtectedButton>
                        <ReadOnlyProtectedButton
                            id="button-disable-all-entries"
                            onClick={disableAllEntriesClickHandler}
                            disabled={roomData?.secondaryStation != null || 
                                scheduleEntries.data.filter((se) => isScheduleInFloor( se.scheduleEntry, floorId, missions, rooms ) && se.scheduleEntry.enabled).length === 0}
                            startIcon={matchesSmUp && <DisableEntriesIcon />}
                            sx={{ fontSize: matchesSmUp ? "medium" : "small" }}
                        >{t("scheduler-disable-all-entries")}</ReadOnlyProtectedButton>
                    </React.Fragment>
                ) : (
                    <Alert severity="warning" sx={{ width: "100%" }}>
                        {t("warning-cannot-create-schedules-for-transition-room")}
                    </Alert>
                )}
            </Stack>

            <CustomSnackbar
                title={t("error-schedule-entries-saving")}
                open={isErrorSetOpen(schedulesRestSet)}
                onClose={errorSnackBarCloseHandler}
                textMessages={getSaveErrorMessages(schedulesRestSet.deleteError ?? schedulesRestSet.patchError ?? schedulesRestSet.postError)}
                severity="error"
            />
            {scheduleEntries.rest.patchError && isHttpError(scheduleEntries.rest.patchError) && (
                <CustomSnackbar
                    title={scheduleEntries.rest.patchError.message}
                    open={scheduleEntries.rest.patchError && scheduleEntries.rest.patchError.message != null}
                    onClose={errorSnackBarCloseHandler}
                    severity="error"
                />
            )} 
            <CustomSnackbar
                title={t("success-schedule-entries-saving")}
                severity="success"
                open={isSuccessSetOpen(schedulesRestSet)}
                onClose={savingSnackbarCloseHandler}
            />
            <CustomSnackbar
                title={t("warning-schedule-entries-changed-may-start")}
                severity="warning"
                open={isSuccessSetOpen(schedulesRestSet) && hasNewCurrentScheduleEntry}
                onClose={savingSnackbarCloseHandler}
            />
        </React.Fragment>
    );
};

export default SchedulerActions;